import Link from "next/link";
import Image from "next/image";
import { ArrowRight, BookOpen, Clock4 } from "lucide-react";
import { useFetchBlogs } from "../blogs/_hook";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "@/hooks/useTranslation";
import { BlogPost } from "@/types/blogs";
import { SupportedLanguage } from "@/config/store/globalStore";
import { BLUR_URL } from "@/utils/images";

export default function RecentBlogs() {
  const { data: blogs, loading: blogsLoading } = useFetchBlogs('');
  const { language } = useLanguage();
  const { t } = useTranslation();

  return (
    <section className="mb-20">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('recent_blogs_title')}</h2>
        <Link
          href={`/${language}/blogs`}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
        >
          {t('view_all')}
          <ArrowRight className="ml-1 w-4 h-4" />
        </Link>
      </div>

      {blogsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 gap-y-12">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/3] bg-gray-200 rounded-xl mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 gap-y-12">
          {blogs?.result?.slice(0, 4).map((blog: BlogPost) => (
            <Link
              key={blog.id}
              href={`/${language}/blogs/${blog.id}`}
              className="group block"
            >
              <div className="mb-4 overflow-hidden rounded-xl relative h-48 sm:h-52 md:h-48 lg:h-48">
                {blog.thumbnail_url ? (
                  <Image
                    src={blog.thumbnail_url}
                    alt={blog.title[language as SupportedLanguage]}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                    placeholder="blur"
                    blurDataURL={BLUR_URL}
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <BookOpen className="w-12 h-12" />
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>
                  {blog.title[language as SupportedLanguage]}
                </h3>
                {/* <p className="text-sm text-gray-600 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>
                  {blog.subtitle[language as SupportedLanguage]}
                </p> */}
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Clock4 className="w-[18px] h-[18px] fill-gray-500 stroke-white"/>
                  <span>{blog.read_time || 5}{t('min_read')}</span>
                  {/* <span>•</span>
                  <span>{blog.views?.toLocaleString() || 0} 조회</span> */}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
} 